import { useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import {
  AlertMessage,
  useAlertMessage,
} from 'hooks/alertMessage/useAlertMessage';
import { useApi } from 'hooks/ApiStoreContext';
import { ApurementDossierFormValues } from 'pages/admin-fonctionnel/AdministrationAvancee/ApurementDossier/types';
import { useGetApurementDossierReport } from 'pages/admin-fonctionnel/AdministrationAvancee/ApurementDossier/useGetApurementDossierReport';
import { getErrorDtoOrThrow } from 'typeGuards/typeGuards';

type SubmitApurementRet = {
  submitApurement: (values: ApurementDossierFormValues) => void;
  isPending: boolean;
  submitAlertMessage?: AlertMessage;
  getReportAlertMessage?: AlertMessage;
};

export function useSubmitApurementDossier(): SubmitApurementRet {
  const { AdminFonctionnelControllerApi: controller } = useApi();
  const {
    alertMessage: submitAlertMessage,
    setErrorMessage,
    setInfoMessage,
    resetAlertMessage,
  } = useAlertMessage();

  const [reportId, setReportId] = useState<string | null>(null);
  const getApurementReportMutation = useGetApurementDossierReport(reportId);

  const submitApurementMutation = useMutation<string | void, Response, File>({
    onMutate: () => {
      setReportId(null);
      resetAlertMessage();
      getApurementReportMutation.resetAlertMessage();
    },
    mutationFn: file => controller.uploadApurementDossiersFileUsingPOST(file),
    onSuccess: data => {
      if (data) {
        setReportId(data);
        setInfoMessage(
          "Le fichier d'apurement a été soumis avec succès. Le résultat sera disponible sous peu.",
        );
      }
    },
    onError: async error => {
      try {
        const errorBody = getErrorDtoOrThrow(await error.json());
        setErrorMessage(errorBody.message);
      } catch {
        setErrorMessage(
          'Une erreur est survenue lors du traitement du fichier.',
        );
      }
    },
  });

  const submitApurement = (values: ApurementDossierFormValues) => {
    if (!values.file) {
      throw new Error('Apurement file is missing');
    }
    submitApurementMutation.mutate(values.file);
  };

  return {
    submitAlertMessage,
    getReportAlertMessage: getApurementReportMutation.alertMessage,
    submitApurement,
    isPending: submitApurementMutation.isPending,
  };
}
